/**
 * Order patch reducer for the changes feed (feature 334, contract
 * specs/334-light-kitchen-sync/contracts/orders-changes.md). Pure and
 * immutable. Shared by the kitchen screen and the waiter app: the copy under
 * kitchen/expo-app/src/lib/sync must stay byte for byte identical.
 */

export interface SyncOrder {
  id: string;
  priority?: number | null;
  createdAt?: string | number | null;
}

export interface ChangesResponse<T extends SyncOrder = SyncOrder> {
  resync: boolean;
  cursor: string;
  orders?: readonly T[];
  removedOrderIds?: readonly string[];
}

export interface ApplyResult<T extends SyncOrder = SyncOrder> {
  list: readonly T[];
  needsFullReload: boolean;
  cursor: string;
}

export type OrderComparator<T extends SyncOrder = SyncOrder> = (a: T, b: T) => number;

const time = (v: string | number | null | undefined): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
};

/** Server order: priority descending, then createdAt descending (newest first). */
export function defaultOrderComparator(a: SyncOrder, b: SyncOrder): number {
  const byPriority = (b.priority ?? 0) - (a.priority ?? 0);
  if (byPriority !== 0) return byPriority;
  return time(b.createdAt) - time(a.createdAt);
}

/**
 * Applies one feed response to the current list.
 *
 * - `resync: true` -> the list is returned untouched and a full reload is asked.
 * - Orders are replaced by id or added; ids in `removedOrderIds` are dropped
 *   (removal wins if an id is in both).
 * - Repeats of one id inside the response are ignored (the first one wins).
 * - Untouched orders keep their relative order; changed orders are merged in at
 *   the position `compare` gives them (after equal orders already in the list).
 */
export function applyChanges<T extends SyncOrder>(
  list: readonly T[],
  response: ChangesResponse<T>,
  compare: OrderComparator<T> = defaultOrderComparator,
): ApplyResult<T> {
  if (response.resync) {
    return { list, needsFullReload: true, cursor: response.cursor };
  }
  const removed = new Set(response.removedOrderIds ?? []);
  const changed = new Map<string, T>();
  for (const order of response.orders ?? []) {
    if (!removed.has(order.id) && !changed.has(order.id)) changed.set(order.id, order);
  }
  if (changed.size === 0 && removed.size === 0) {
    return { list, needsFullReload: false, cursor: response.cursor };
  }

  const untouched = list.filter((o) => !removed.has(o.id) && !changed.has(o.id));
  const incoming = [...changed.values()].sort(compare);
  const next: T[] = [];
  let i = 0;
  let j = 0;
  while (i < incoming.length && j < untouched.length) {
    if (compare(incoming[i], untouched[j]) < 0) next.push(incoming[i++]);
    else next.push(untouched[j++]);
  }
  while (i < incoming.length) next.push(incoming[i++]);
  while (j < untouched.length) next.push(untouched[j++]);
  return { list: next, needsFullReload: false, cursor: response.cursor };
}
